// /src/pages/PlantWiki.jsx
import React, { useState, useEffect } from 'react'
import { useSearchParams, useLocation } from 'react-router-dom'
import { Search, Filter, Grid, List, Book, Trees, Flower, TreeDeciduous, Palmtree, LeafyGreen, GitBranch } from 'lucide-react'
import PlantCard from '../components/PlantCard'
import usePlantStore from '../utils/plantStore'

const PlantWiki = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [viewMode, setViewMode] = useState('grid')
  const { plants, fetchPlants } = usePlantStore()
  const location = useLocation()

  // Fetch plants on component mount
  useEffect(() => {
    fetchPlants()
  }, [fetchPlants])

  // Handle URL search parameters
  useEffect(() => {
    const urlSearch = searchParams.get('search')
    const urlCategory = searchParams.get('category')
    
    if (urlSearch) {
      setSearchTerm(urlSearch)
    }
    
    if (urlCategory) {
      setSelectedCategory(urlCategory)
    }
  }, [searchParams])

  // Scroll to top when location changes (route navigation)
  useEffect(() => {
    // Multiple aggressive approaches to ensure scroll to top
    const scrollToTop = () => {
      // Method 1: Immediate scroll
      window.scrollTo(0, 0)
      
      // Method 2: Document element scroll
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      
      // Method 3: Force scroll after a brief delay
      setTimeout(() => {
        window.scrollTo(0, 0)
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      }, 50)
      
      // Method 4: Smooth scroll as final attempt
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    }
    
    // Execute immediately
    scrollToTop()
    
    // Also execute after a longer delay to catch any late overrides
    setTimeout(scrollToTop, 200)
    setTimeout(scrollToTop, 500)
  }, [location.pathname])

  const categories = [
    { id: 'all', name: 'All Plants', icon: Book, count: plants.length },
    { id: 'trees', name: 'Trees', icon: Trees, count: plants.filter(p => p.category === 'trees').length },
    { id: 'flowers', name: 'Flowers', icon: Flower, count: plants.filter(p => p.category === 'flowers').length },
    { id: 'succulents', name: 'Succulents', icon: TreeDeciduous, count: plants.filter(p => p.category === 'succulents').length },
    { id: 'palms', name: 'Palms', icon: Palmtree, count: plants.filter(p => p.category === 'palms').length },
    { id: 'ferns', name: 'Ferns', icon: LeafyGreen, count: plants.filter(p => p.category === 'ferns').length },
    { id: 'climbers', name: 'Climbers', icon: GitBranch, count: plants.filter(p => p.category === 'climbers').length },
  ]

  const filteredPlants = plants.filter(plant => {
    const matchesSearch = plant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plant.scientificName.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || plant.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  return (
    <div className="min-h-screen bg-forest-50">
      {/* Header */}
      <div className="bg-white border-b border-forest-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-forest-900 mb-4">PlantWiki</h1>
            <p className="text-xl text-forest-600 max-w-2xl mx-auto mb-2">
              Discover and learn about thousands of plants from around the world. Find care guides, habitat information, and detailed descriptions—all in PlantWiki, powered by PalmGarden.
            </p>
            <p className="text-sm text-forest-400">PalmGarden is your home for plant knowledge and conservation.</p>
          </div>

          {/* Search and Filters */}
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-forest-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search plants by name or scientific name..."
                  value={searchTerm}
                  onChange={(e) => {
                    const newSearchTerm = e.target.value
                    setSearchTerm(newSearchTerm)
                    // Update URL with search parameter
                    if (newSearchTerm.trim()) {
                      setSearchParams({ search: newSearchTerm })
                    } else {
                      setSearchParams({})
                    }
                  }}
                  className="input-field pl-10"
                />
              </div>
              <button className="btn-secondary inline-flex items-center space-x-2">
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id)
                    setSearchParams({ category: category.id })
                  }}
                  className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-primary-100 text-primary-700 border border-primary-200'
                      : 'bg-white text-forest-600 border border-forest-200 hover:bg-forest-50'
                  }`}
                >
                  <category.icon className="w-4 h-4" />
                  <span>{category.name}</span>
                  <span className="bg-forest-100 text-forest-600 px-2 py-0.5 rounded-full text-xs">
                    {category.count}
                  </span>
                </button>
              ))}
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-white text-forest-400 hover:bg-forest-50'
                  }`}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-white text-forest-400 hover:bg-forest-50'
                  }`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
              <div className="text-sm text-forest-500">
                {filteredPlants.length} of {plants.length} plants
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Plants Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filteredPlants.length > 0 ? (
          <div className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
              : 'grid-cols-1'
          }`}>
            {filteredPlants.map((plant) => (
              <PlantCard key={plant.id} plant={plant} viewMode={viewMode} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Book className="w-16 h-16 text-forest-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-forest-900 mb-2">
              {searchTerm ? `No plants found for "${searchTerm}"` : 'No plants found'}
            </h3>
            <p className="text-forest-600 mb-4">
              {searchTerm 
                ? 'Try different search terms or browse by category.'
                : 'Try adjusting your search terms or category filters.'
              }
            </p>
            {searchTerm && (
              <div className="flex flex-wrap justify-center gap-2">
                <button 
                  onClick={() => {
                    setSearchTerm('')
                    setSearchParams({})
                  }}
                  className="btn-secondary inline-flex items-center space-x-2"
                >
                  <Search className="w-4 h-4" />
                  <span>Clear Search</span>
                </button>
                <button 
                  onClick={() => setSelectedCategory('all')}
                  className="btn-secondary inline-flex items-center space-x-2"
                >
                  <Book className="w-4 h-4" />
                  <span>Show All Plants</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PlantWiki 